import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '../Button/Button';
import { AlertDialog } from '../AlertDialog/AlertDialog';
import { Toast, toast } from '../Toast';
import { MasterCrudTable } from './MasterCrudTable';
import { MasterCrudForm } from './MasterCrudForm';
import { PlusIcon, PencilIcon, TrashIcon } from './icons';
import type { MasterCrudDetailConfig, MasterCrudAction } from './MasterCrud.types';
import type { ToastColor } from '../Toast/Toast.types';

// ============================================
// TIPOS INTERNOS
// ============================================

interface MasterCrudDetailSectionProps<D = any> {
  /** ID del registro maestro al que pertenecen estos detalles */
  masterId: string | number;
  /** Configuración completa del detalle */
  config: MasterCrudDetailConfig<D>;
  /** Función de traducción inyectada por el padre */
  t: (key: string, defaultValue?: string) => string;
  /** Idioma para formatos (ISO 639-1) */
  locale?: string;
  /**
   * Si este componente se renderiza dentro de un modal padre.
   * Afecta el z-index de los modales internos para evitar solapamiento.
   * @default false
   */
  isInsideModal?: boolean;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

/**
 * MasterCrudDetailSection — Sección embebida de registros de detalle.
 *
 * Se renderiza dentro del formulario del maestro (modo página o modal expandido)
 * y permite gestionar los registros hijos sin salir de la pantalla actual.
 *
 * Características:
 * - Carga y muestra los detalles del maestro activo
 * - Permite crear, editar y eliminar detalles desde modales internos
 * - Auto-inyecta el foreignKey al crear nuevos registros
 * - Completamente auto-contenido (no depende del estado del padre)
 *
 * @see MasterCrudDetailConfig para la configuración completa
 */
export const MasterCrudDetailSection = <D extends Record<string, any>>({
  masterId,
  config,
  t,
  locale = 'es',
  isInsideModal = false,
}: MasterCrudDetailSectionProps<D>) => {
  const {
    title,
    entityName = '',
    fields,
    service,
    foreignKey,
    idField = 'id',
    showCreateButton = true,
    pageSize = 5,
    emptyMessage,
    allowEdit = true,
    allowDelete = true,
    permissions,
    actions: customActions = [],
    formWidth = '600px',
    formColumns = 2,
    createButtonText,
    saveButtonText,
    cancelButtonText,
    onDetailCreateSuccess,
    onDetailUpdateSuccess,
    onDetailDeleteSuccess,
  } = config;

  const canCreate = permissions?.canCreate ?? showCreateButton;
  const canUpdate = permissions?.canUpdate ?? allowEdit;
  const canDelete  = permissions?.canDelete ?? allowDelete;

  // ===== ESTADO =====
  const [data, setData] = useState<D[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [detailViewState, setDetailViewState] = useState<'LIST' | 'CREATE' | 'EDIT'>('LIST');
  const [selectedDetail, setSelectedDetail] = useState<D | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; record: D | null }>({
    show: false,
    record: null,
  });
  const [toastConfig, setToastConfig] = useState<{ show: boolean; message: string; color: ToastColor }>({
    show: false,
    message: '',
    color: 'green',
  });

  const formRef = useRef<any>(null);

  // ===== HELPERS =====
  const showToast = useCallback((message: string, color: ToastColor = 'green') => {
    setToastConfig({ show: true, message, color });
    setTimeout(() => setToastConfig(prev => ({ ...prev, show: false })), 4000);
  }, []);

  // ===== CARGA DE DATOS =====
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await service.getAll({
        page: currentPage,
        limit: pageSize,
        filters: { [foreignKey]: masterId },
      });
      setData(response.data);
      setTotal(response.total);
    } catch (err: any) {
      const msg = err?.msgError || err?.detail || err?.message
        || t('errorDataLoad', 'Error al cargar los registros');
      showToast(msg, 'red');
    } finally {
      setLoading(false);
    }
  }, [masterId, currentPage, pageSize, foreignKey, service, showToast, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ===== HANDLERS CRUD =====
  const handleCreate = useCallback(() => {
    setSelectedDetail(null);
    setDetailViewState('CREATE');
  }, []);

  const handleEdit = useCallback((record: D) => {
    setSelectedDetail(record);
    setDetailViewState('EDIT');
  }, []);

  const handleCancel = useCallback(() => {
    setDetailViewState('LIST');
    setSelectedDetail(null);
  }, []);

  const handleSubmit = useCallback(async (formData: Partial<D>) => {
    setFormLoading(true);
    try {
      if (detailViewState === 'CREATE') {
        // Inyectar automáticamente el foreignKey con el ID del maestro
        const dataWithFk = { ...formData, [foreignKey]: masterId } as Partial<D>;
        const created = await service.create(dataWithFk);
        onDetailCreateSuccess?.(created);
        showToast(t('successCreate', 'Registro creado correctamente'), 'green');
      } else if (detailViewState === 'EDIT' && selectedDetail) {
        const recordId = (selectedDetail as any)[idField];
        const updated = await service.update(recordId, formData);
        onDetailUpdateSuccess?.(updated);
        showToast(t('successUpdate', 'Registro actualizado correctamente'), 'green');
      }
      await loadData();
      setDetailViewState('LIST');
      setSelectedDetail(null);
    } catch (err: any) {
      const msg = err?.msgError || err?.detail || err?.message
        || t('defaultError', 'Ha ocurrido un error inesperado');
      showToast(msg, 'red');
    } finally {
      setFormLoading(false);
    }
  }, [
    detailViewState, selectedDetail, idField, foreignKey, masterId,
    service, loadData, showToast, t, onDetailCreateSuccess, onDetailUpdateSuccess,
  ]);

  const handleDeleteClick = useCallback((record: D) => {
    setDeleteConfirm({ show: true, record });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirm.record) return;
    const recordId = (deleteConfirm.record as any)[idField];
    try {
      await service.delete(recordId);
      onDetailDeleteSuccess?.(recordId);
      showToast(t('successDelete', 'Registro eliminado correctamente'), 'green');
      setDeleteConfirm({ show: false, record: null });
      await loadData();
    } catch (err: any) {
      const msg = err?.msgError || err?.detail || err?.message
        || t('defaultError', 'Ha ocurrido un error inesperado');
      showToast(msg, 'red');
    }
  }, [deleteConfirm.record, idField, service, loadData, showToast, t, onDetailDeleteSuccess]);

  // ===== ACCIONES DE LA TABLA =====
  const actionsList: MasterCrudAction<D>[] = [
    ...(canUpdate ? [{
      id: 'edit',
      name: t('edit', 'Editar'),
      icon: <PencilIcon className="w-4 h-4" />,
      action: (r: D | D[]) => handleEdit(r as D),
      order: 100,
    }] : []),
    ...(canDelete ? [{
      id: 'delete',
      name: t('delete', 'Eliminar'),
      icon: <TrashIcon className="w-4 h-4" />,
      action: (r: D | D[]) => handleDeleteClick(r as D),
      order: 200,
    }] : []),
    ...customActions,
  ].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const totalPages = Math.ceil(total / pageSize);

  // z-index de los modales internos: más alto cuando estamos dentro de un modal padre
  const innerModalZ = isInsideModal ? 'z-[70]' : 'z-[60]';

  // Título resuelto
  const sectionTitle = title || t('details', 'Detalles');
  const addLabel = createButtonText
    || `${t('add', 'Agregar')}${entityName ? ` ${entityName}` : ''}`;

  // ===== RENDER =====
  return (
    <>
      {/* Toast de operaciones del detalle (posición distinta al toast del maestro) */}
      {toastConfig.show && (() => {
        const { message, color } = toastConfig;
        const toastType = color === 'red' ? 'error' : color === 'green' ? 'success' : color === 'orange' ? 'warning' : 'info';

        setTimeout(() => {
          if (toastType === 'error') {
            toast.error('Error', { description: message });
          } else if (toastType === 'success') {
            toast.success('Correcto', { description: message });
          } else if (toastType === 'warning') {
            toast.warning('Advertencia', { description: message });
          } else {
            toast('Información', { description: message });
          }
          setToastConfig(prev => ({ ...prev, show: false }));
        }, 0);

        return null;
      })()
      }

      {/* ─── Sección de Detalle ──────────────────────────────────────────── */}
      <div className="mt-6">

        {/* Separador visual con título flotante */}
        <div className="relative flex items-center mb-5">
          <div className="flex-grow border-t border-border-primary dark:border-dark-border-primary" />
          <span className="mx-3 text-xs font-semibold uppercase tracking-widest text-content-secondary dark:text-content-secondary select-none">
            {sectionTitle}
          </span>
          <div className="flex-grow border-t border-border-primary dark:border-dark-border-primary" />
        </div>

        {/* Header: título con badge de total + botón agregar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-content-primary dark:text-dark-content-primary">
              {sectionTitle}
            </h3>
            {total > 0 && (
              <span className={[
                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold',
                'bg-primary-custom-50 text-primary-custom-600',
                'dark:bg-dark-bg-inverse/20 dark:text-primary-custom-400',
              ].join(' ')}>
                {total}
              </span>
            )}
          </div>

          {canCreate && (
            <Button
              type="outline"
              size="sm"
              leftIcon={<PlusIcon className="w-4 h-4" />}
              onClick={handleCreate}
            >
              {addLabel}
            </Button>
          )}
        </div>

        {/* Tabla de detalles */}
        <div className="rounded-xl overflow-hidden border border-border-primary dark:border-dark-border-primary">
          <MasterCrudTable
            fields={fields}
            data={data}
            loading={loading}
            emptyMessage={emptyMessage || t('empty', 'No hay registros disponibles')}
            showRowActions={canUpdate || canDelete || customActions.length > 0}
            actions={actionsList}
            pagination={totalPages > 1 ? {
              currentPage,
              totalPages,
              onPageChange: setCurrentPage,
              pageSize,
              totalRecords: total,
              previousLabel: t('previous', 'Anterior'),
              nextLabel: t('next', 'Siguiente'),
              perPageLabel: t('perPage', 'por pág.'),
              recordsLabel: t('records', 'registros'),
              goToPageLabel: t('goToPage', 'Pág.'),
            } : undefined}
            t={t}
            locale={locale}
          />
        </div>
      </div>

      {/* ─── Modal de Crear / Editar Detalle ───────────────────────────────── */}
      {(detailViewState === 'CREATE' || detailViewState === 'EDIT') && (
        <div
          className={`fixed inset-0 ${innerModalZ} flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200`}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full bg-white dark:bg-dark-bg-primary rounded-xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh] animate-in zoom-in-95 duration-200"
            style={{ maxWidth: formWidth }}
          >
            <MasterCrudForm
              ref={formRef}
              fields={fields as any}
              entityName={entityName}
              initialData={selectedDetail || undefined}
              isEditing={detailViewState === 'EDIT'}
              loading={formLoading}
              onSubmit={handleSubmit as any}
              onCancel={handleCancel}
              saveButtonText={saveButtonText || t('save', 'Guardar')}
              cancelButtonText={cancelButtonText || t('cancel', 'Cancelar')}
              columns={formColumns}
              width="100%"
              idField={idField}
              className="flex-1 min-h-0"
              t={t}
            />
          </div>
        </div>
      )}

      {/* ─── Confirmación de Eliminación ───────────────────────────────────── */}
      {deleteConfirm.show && (
        <div className={`fixed inset-0 ${innerModalZ} flex items-center justify-center bg-black/50`}>
          <AlertDialog
            title={t('confirmDeleteTitle', '¿Estás seguro de eliminar este registro?')}
            onCancel={() => setDeleteConfirm({ show: false, record: null })}
            onConfirm={handleDeleteConfirm}
            cancelText={t('cancelText', 'Cancelar')}
            confirmText={t('confirmDeleteText', 'Eliminar')}
          >
            <p className="text-sm leading-5 font-normal text-content-tertiary dark:text-content-tertiary">
              {t('confirmDeleteDesc', 'Esta acción no se puede deshacer.')}
            </p>
          </AlertDialog>
        </div>
      )}
    </>
  );
};
